import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, gql } from '@apollo/client';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../config/firebase';
import logo from '../images/logo.png';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

// GraphQL queries and mutations
const VALIDATE_MAGIC_LINK = gql`
  query ValidateMagicLink($token: String!) {
    validateMagicLink(token: $token) {
      valid
      planId
      participantId
      participantName
      planGoal
      error
    }
  }
`;

const CONSUME_MAGIC_LINK = gql`
  mutation ConsumeMagicLink($token: String!) {
    consumeMagicLink(token: $token) {
      valid
      firebaseToken
      planId
      participantId
      participantName
      planGoal
      error
    }
  }
`;

type JoinState = 'loading' | 'valid' | 'invalid' | 'joining' | 'success' | 'error';

export default function JoinPage() {
  useDocumentTitle('Join');

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [state, setState] = useState<JoinState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [planInfo, setPlanInfo] = useState<{
    planId?: string;
    participantName?: string;
    planGoal?: string;
  }>({});

  // Validate magic link on mount
  const { loading: validating } = useQuery(VALIDATE_MAGIC_LINK, {
    variables: { token: token || '' },
    skip: !token,
    onCompleted: (data) => {
      if (data.validateMagicLink.valid) {
        setState('valid');
        setPlanInfo({
          planId: data.validateMagicLink.planId,
          participantName: data.validateMagicLink.participantName,
          planGoal: data.validateMagicLink.planGoal,
        });
      } else {
        setState('invalid');
        setErrorMessage(data.validateMagicLink.error || 'Invalid or expired link');
      }
    },
    onError: (error) => {
      setState('error');
      setErrorMessage(error.message || 'Failed to validate link');
    },
  });

  // Consume magic link mutation
  const [consumeMagicLink] = useMutation(CONSUME_MAGIC_LINK);

  // Update state based on validation loading
  useEffect(() => {
    if (!token) {
      setState('invalid');
      setErrorMessage('No invitation token provided');
    } else if (validating) {
      setState('loading');
    }
  }, [token, validating]);

  const handleJoin = async () => {
    if (!token) return;

    setState('joining');
    setErrorMessage('');

    try {
      const { data } = await consumeMagicLink({ variables: { token } });

      if (!data.consumeMagicLink.valid) {
        setState('error');
        setErrorMessage(data.consumeMagicLink.error || 'Failed to join plan');
        return;
      }

      // Sign in with the custom token
      const firebaseToken = data.consumeMagicLink.firebaseToken;
      if (!firebaseToken) {
        setState('error');
        setErrorMessage('No authentication token received');
        return;
      }

      await signInWithCustomToken(auth, firebaseToken);

      setState('success');

      // Redirect to plan after a short delay
      setTimeout(() => {
        navigate(`/app/plans/${data.consumeMagicLink.planId}`);
      }, 1500);
    } catch (error: any) {
      console.error('Error joining plan:', error);
      setState('error');
      setErrorMessage(error.message || 'Failed to join plan');
    }
  };

  // Render loading state
  if (state === 'loading') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <img
                src={logo}
                alt="Planapus Logo"
                style={{ height: '50px', width: 'auto', marginBottom: '16px' }}
              />
            </Box>
            <Box sx={{ px: 2 }}>
              <Skeleton variant="text" height={40} />
              <Skeleton variant="rectangular" height={80} sx={{ my: 2 }} />
              <Skeleton variant="text" height={30} />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Render invalid/error state
  if (state === 'invalid' || state === 'error') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <img
                src={logo}
                alt="Planapus Logo"
                style={{ height: '50px', width: 'auto', marginBottom: '16px' }}
              />
            </Box>

            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                {state === 'invalid' ? 'Invalid Invitation' : 'Something Went Wrong'}
              </Typography>
            </Box>

            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
              The invitation link may have expired or already been used.
              Please contact the plan owner to request a new invitation.
            </Typography>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/')}
              >
                Go to Home
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Render success state
  if (state === 'success') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Welcome, {planInfo.participantName}!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                You've successfully joined the plan.
              </Typography>
              <CircularProgress size={24} sx={{ mt: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Redirecting to your plan...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Render valid state (join preview)
  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Card>
        <CardContent sx={{ py: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img
              src={logo}
              alt="Planapus Logo"
              style={{ height: '50px', width: 'auto', marginBottom: '16px' }}
            />
          </Box>

          <Typography variant="h5" gutterBottom sx={{ textAlign: 'center' }}>
            Hi {planInfo.participantName}!
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
            You've been invited to collaborate on a plan:
          </Typography>

          <Card variant="outlined" sx={{ mb: 4, bgcolor: 'grey.50' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {planInfo.planGoal}
              </Typography>
            </CardContent>
          </Card>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            Click the button below to join and view your assigned tasks.
          </Typography>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleJoin}
              disabled={state === 'joining'}
              sx={{ minWidth: 200 }}
            >
              {state === 'joining' ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                  Joining...
                </>
              ) : (
                'Join Plan'
              )}
            </Button>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 3 }}
          >
            By joining, you'll be able to view the plan and update tasks assigned to you.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
}
